import { useState, useEffect } from 'react'
import { Select as SelectBase, SizableText, XStack, YStack } from '@mythical/ui'
import { LinearGradient } from '@tamagui/linear-gradient'
import { IconChevronDown, IconChevronUp, IconCheckCircle } from '../Icon'
import { interaction } from '@mythical/ui'

export type TitleSelectProps = {
  id: string
  value: string
  options: { label: string; value: string }[]
  onValueChange: (value: string) => void
}

export function TitleSelect({
  id,
  value,
  options,
  onValueChange,
}: TitleSelectProps) {
  const [selectVal, setSelectVal] = useState<string>(value)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (value !== selectVal) {
      setSelectVal(value)
    }
  }, [value])

  const selectedLabel = options.find((o) => o.value === selectVal)?.label || ''

  return (
    <SelectBase
      id={id}
      value={selectVal}
      onValueChange={(val) => {
        setSelectVal(val)
        onValueChange(val)
      }}
      onOpenChange={setIsOpen}
    >
      <SelectBase.Trigger
        theme="transparent"

        p={0}
        m={0}
        minWidth="auto"
        maxWidth="none"
        h='auto'
        mih='auto'
        {...interaction.opacity}

      >
        <XStack ai="center" gap="$1.5">
          <SizableText
            ff="$heading"
            size="$s"
            $gtSm={{ size: '$m' }}
            color="$fgPrimary"
          >
            {selectedLabel}
          </SizableText>
          <YStack animation="quick" rotate={isOpen ? '180deg' : '0deg'}>
            <IconChevronDown size={18} pos='relative' color='$fgSecondary' top={1} />
          </YStack>
        </XStack>
      </SelectBase.Trigger>

      <SelectBase.Adapt platform="native">
        <SelectBase.Sheet modal dismissOnSnapToBottom animation="normal">
          <SelectBase.Sheet.Frame theme="bgSecondary" p="$2" pt="$3">
            <SelectBase.Sheet.Handle bg="$borderSecondary" />
            <SelectBase.Sheet.ScrollView>
              <SelectBase.Adapt.Contents />
            </SelectBase.Sheet.ScrollView>
          </SelectBase.Sheet.Frame>
          <SelectBase.Sheet.Overlay
            bg="$backdrop"
            animation="quick"
            enterStyle={{ o: 0 }}
            exitStyle={{ o: 0 }}
          />
        </SelectBase.Sheet>
      </SelectBase.Adapt>

      <SelectBase.Content zIndex={200000}>
        <SelectBase.ScrollUpButton
          ai="center"
          jc="center"
          pos="absolute"
          w="100%"
          h="$3"
          overflow="hidden"
        >
          <YStack zi={10}>
            <IconChevronUp color="$fgPrimary" size={20} />
          </YStack>
          <LinearGradient
            start={[0, 0]}
            end={[0, 1]}
            fullscreen
            colors={['$bgSecondary', 'transparent']}
            br="$4"
            top="$0.25"
          />
        </SelectBase.ScrollUpButton>

        <SelectBase.Viewport minWidth={220} theme="bgSecondaryOutlined" p="$2">
          <SelectBase.Group theme="bgSecondaryOutlined">
            {options.map((item, i) => {
              return (
                <SelectBase.Item
                  br="$3"
                  index={i}
                  key={`${item.value}`}
                  value={item.value}
                  py="$2.5"
                  px="$3"
                  cursor="pointer"
                >
                  <SelectBase.ItemText
                    flexShrink={1}
                    fontWeight="600"
                    size="$4"
                  >
                    {item.label}
                  </SelectBase.ItemText>
                  <SelectBase.ItemIndicator flexShrink={0} ml="auto">
                    <IconCheckCircle size={16} />
                  </SelectBase.ItemIndicator>
                </SelectBase.Item>
              )
            })}
          </SelectBase.Group>
        </SelectBase.Viewport>

        <SelectBase.ScrollDownButton
          ai="center"
          jc="center"
          pos="relative"
          w="100%"
          h="$3"
          overflow="hidden"
        >
          <YStack zi={10}>
            <IconChevronDown size={20} color="$fgPrimary" />
          </YStack>
          <LinearGradient
            start={[0, 0]}
            end={[0, 1]}
            fullscreen
            colors={['transparent', '$bgSecondary']}
            br="$4"
            bottom="$0.25"
          />
        </SelectBase.ScrollDownButton>
      </SelectBase.Content>
    </SelectBase>
  )
}
